import React from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, staggerItem } from '../lib/animations';
import { PlasmaWeb } from './ui/cosmic-plasma-web';
import { useI18n } from '../i18n';
import { trackNavClick, trackInstagramClick } from '../lib/analytics';

export default function Hero() {
  const { language, setLanguage, languages, t } = useI18n();

  return (
    <section className="relative w-full min-h-screen bg-black overflow-hidden">

      {/* Full-section plasma background */}
      <div className="absolute inset-0 z-0">
        <PlasmaWeb
          hueShift={230}
          hueMin={0.57}
          hueMax={0.82}
          density={0.75}
          glowIntensity={0.65}
          saturation={0.85}
          brightness={0.75}
          energyFlow={0.85}
          pulseIntensity={0.18}
          attractionStrength={1.4}
          mouseAttraction={true}
          mouseInteraction={true}
          speed={0.45}
          transparent={false}
        />
      </div>

      {/* Dark vignette overlay — keeps text readable */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 60% 50%, transparent 20%, rgba(0,0,0,0.55) 65%, rgba(0,0,0,0.85) 100%)',
        }}
      />
      {/* Bottom fade into next section */}
      <div
        className="absolute inset-x-0 bottom-0 h-40 z-10 pointer-events-none"
        style={{ background: 'linear-gradient(to top, #000 0%, transparent 100%)' }}
      />

      {/* Navigation */}
      <motion.header
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="relative z-20 flex flex-wrap items-center justify-between gap-3 px-6 md:px-12 pt-8"
      >
        <div className="text-white font-bold tracking-wide text-base md:text-lg">
          {t('brand.name')}<span className="text-blue-400/60">•</span>{t('brand.role')}
        </div>
        <nav className="hidden md:flex items-center gap-10 text-sm text-white/55">
          <a href="#about"   onClick={() => trackNavClick('about')}   className="hover:text-white transition-colors duration-200">{t('nav.about')}</a>
          <a href="#process" onClick={() => trackNavClick('process')} className="hover:text-white transition-colors duration-200">{t('nav.process')}</a>
          <a href="#budget"  onClick={() => trackNavClick('budget')}  className="hover:text-white transition-colors duration-200">{t('nav.budget')}</a>
          <a href="#faq"     onClick={() => trackNavClick('faq')}     className="hover:text-white transition-colors duration-200">{t('nav.faq')}</a>
        </nav>
        <div className="flex items-center rounded-full border border-white/10 bg-white/[0.04] p-1">
          {languages.map((item) => (
            <button
              key={item.code}
              type="button"
              onClick={() => setLanguage(item.code)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors duration-200 ${
                language === item.code ? 'bg-white text-black' : 'text-white/45 hover:text-white'
              }`}
              aria-pressed={language === item.code}
            >
              {item.label}
            </button>
          ))}
        </div>
        <motion.a
          href="https://www.instagram.com/mysyshyn_target/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackInstagramClick('header')}
          whileHover={{ scale: 1.03, boxShadow: '0 0 28px rgba(168,85,247,0.3)' }}
          whileTap={{ scale: 0.97 }}
          transition={{ duration: 0.18 }}
          className="inline-flex items-center gap-2 rounded-full text-white text-sm font-medium px-5 py-2.5 transition-all duration-200"
          style={{
            background: 'linear-gradient(135deg, #1465eb 0%, #a855f7 50%, #cf2cf0 100%)',
            boxShadow: '0 0 20px rgba(168,85,247,0.25), inset 0 1px 0 rgba(255,255,255,0.1)',
          }}
        >
          {t('hero.topCta')}
          <span aria-hidden>↗</span>
        </motion.a>
      </motion.header>

      {/* Hero content — centred */}
      <div className="relative z-20 flex flex-col items-start justify-center min-h-[calc(100vh-80px)] px-6 md:px-16 lg:px-24 max-w-4xl">
        <motion.div
          variants={staggerContainer(0.1, 0.2)}
          initial="hidden"
          animate="visible"
        >
          {/* Label */}
          <motion.div variants={staggerItem} className="mb-7">
            <div
              className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 px-4 py-2 text-xs uppercase tracking-[0.22em] text-white/65 backdrop-blur-md"
              style={{
                background: 'rgba(20, 101, 235, 0.08)',
                boxShadow: '0 0 16px rgba(168,85,247,0.2), inset 0 1px 0 rgba(255,255,255,0.08)',
              }}
            >
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{
                  background: 'linear-gradient(135deg, #1465eb, #a855f7)',
                  boxShadow: '0 0 6px rgba(20,101,235,0.6)',
                }}
              />
              {t('hero.label')}
            </div>
          </motion.div>

          {/* Headline */}
          <motion.h1
            variants={staggerItem}
            className="font-extrabold leading-[1.02] tracking-tight text-white"
            style={{ fontSize: 'clamp(2.8rem, 7vw, 5.5rem)', letterSpacing: '-0.03em' }}
          >
            {t('hero.h1.line1')}<br />
            <span className="text-gradient">{t('hero.h1.line2')}</span>
          </motion.h1>

          {/* Sub */}
          <motion.p
            variants={staggerItem}
            className="mt-7 text-white/50 text-base md:text-lg leading-relaxed max-w-lg"
          >
            {t('hero.sub1')} {t('hero.sub2')}
          </motion.p>

          {/* CTAs */}
          <motion.div variants={staggerItem} className="mt-10 flex flex-wrap items-center gap-5">
            <motion.a
              href="#process"
              onClick={() => trackNavClick('process')}
              whileHover={{ scale: 1.03, boxShadow: '0 0 32px rgba(168,85,247,0.35)' }}
              whileTap={{ scale: 0.97 }}
              transition={{ duration: 0.18 }}
              className="inline-flex items-center gap-2 rounded-full text-white font-medium px-7 py-4 transition-all duration-200"
              style={{
                background: 'linear-gradient(135deg, #1465eb 0%, #a855f7 50%, #cf2cf0 100%)',
                boxShadow: '0 0 24px rgba(168,85,247,0.28), inset 0 1px 0 rgba(255,255,255,0.15)',
              }}
            >
              {t('hero.primaryCta')}
              <span aria-hidden>↗</span>
            </motion.a>
            <motion.a
              href="https://www.instagram.com/mysyshyn_target/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackInstagramClick('hero')}
              whileHover={{ x: 3 }}
              transition={{ duration: 0.2 }}
              className="inline-flex items-center gap-3 text-white/55 hover:text-white transition-colors duration-200"
            >
              <span className="grid place-items-center h-11 w-11 rounded-full border border-white/15 hover:border-blue-400/60 transition-all duration-200 text-sm">
                ▶
              </span>
              {t('hero.secondaryCta')}
            </motion.a>
          </motion.div>

          {/* Footnote */}
          <motion.p
            variants={staggerItem}
            className="mt-6 text-white/30 text-sm"
          >
            {t('hero.note')}
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}
